import i18n from '../i18n';

/**
 * «Έχεις αλλαγές που δεν αποθηκεύτηκαν» — κοινό μητρώο της διαχείρισης καταστήματος (12/10/2026).
 * Κάθε σελίδα με φόρμα δηλώνει εδώ μια συνάρτηση «έχω αλλαγές;» (hooks/useDirtyForm με guardId)· η
 * διαχείριση (StoreAdminDashboard) ρωτά πριν αλλάξει καρτέλα ή αποσυνδεθεί, και ο browser πριν κλείσει
 * ή ανανεώσει τη σελίδα (beforeunload — ένας listener, όσο υπάρχει έστω μία σελίδα).
 * Συνάρτηση και όχι τιμή: η απάντηση διαβάζεται τη στιγμή της ερώτησης, όχι στο τελευταίο render.
 */

type IsDirty = () => boolean;

const sources = new Map<string, IsDirty>();

// Ο χρήστης μόλις απάντησε «ναι, φεύγω» (confirmDiscard) και ακολουθεί πλοήγηση εκτός εφαρμογής
// (π.χ. «Προϊόντα» = /products): όχι δεύτερη ερώτηση από τον browser. Ισχύει ως το επόμενο tick.
let discardConfirmed = false;

const onBeforeUnload = (event: BeforeUnloadEvent) => {
  if (discardConfirmed || !hasUnsavedChanges()) return;
  event.preventDefault();
  // Παλιότεροι Chrome/Safari θέλουν και returnValue· το κείμενο το διαλέγει ο browser.
  event.returnValue = '';
};

/** Επιστρέφει τη διαγραφή της εγγραφής (για το cleanup ενός useEffect). */
export function registerDirtySource(id: string, isDirty: IsDirty): () => void {
  if (sources.size === 0) window.addEventListener('beforeunload', onBeforeUnload);
  sources.set(id, isDirty);
  return () => unregisterDirtySource(id, isDirty);
}

/** Με `isDirty`: σβήνει μόνο αν είναι ακόμη η ίδια εγγραφή — μια νεότερη σελίδα με το ίδιο id μένει. */
export function unregisterDirtySource(id: string, isDirty?: IsDirty): void {
  if (isDirty && sources.get(id) !== isDirty) return;
  sources.delete(id);
  if (sources.size === 0) window.removeEventListener('beforeunload', onBeforeUnload);
}

export function hasUnsavedChanges(): boolean {
  return Array.from(sources.values()).some(isDirty => isDirty());
}

/** true = μπορείς να φύγεις: καμία αλλαγή, ή ο χρήστης δέχτηκε να χαθούν. */
export function confirmDiscard(): boolean {
  if (!hasUnsavedChanges()) return true;
  const leave = window.confirm(i18n.t('settings:unsaved.confirmLeave'));
  if (leave) {
    discardConfirmed = true;
    setTimeout(() => { discardConfirmed = false; }, 0);
  }
  return leave;
}
